"use client";

import React, { useState, useEffect, useMemo } from 'react';
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { partnerLogo } from "@/utils/partnerLogo";

function Logo({ item }) {
  return (
    <div className="mx-2 flex-shrink-0 flex items-center justify-center 
                    h-[80px] w-[140px] bg-white border-[1px] border-[#efefef] rounded-lg">
      <Image
        src={item.img}
        alt={item.altText}
        width={140}
        height={80}
        loading="lazy"
        className="object-contain max-h-[60px] max-w-[120px]"
      />
    </div>
  );
}

const TrustedPartnersSection = () => {
  const [open, setOpen] = useState(false);

  // SLICES FOR 3 LINES
  const row1 = useMemo(() => partnerLogo.slice(0, 50), []);
  const row2 = useMemo(() => partnerLogo.slice(50, 100), []);
  const row3 = useMemo(() => partnerLogo.slice(100, 161), []);

  return (
    <section className="industry-collab-logo-grid relative pb-2">
      <div className="container ">
        <div className="row justify-content-center">
          <div className="col-lg-12 text-center">
            <h2 className="title text-dark">300+ Trusted Partners</h2>
            <p className="text-muted mb-5">
              Celebrating milestones of acknowledgment of our impactful contributions
            </p>
          </div>
        </div>

        <div
          className="hidden sm:block absolute left-0 top-0 h-full w-24 z-20 pointer-events-none"
          style={{ background: "linear-gradient(270deg, rgb(255 255 255 / 5%) 0%, rgb(255 255 255 / 84%) 50%, rgb(255 255 255) 80%)" }}
        />

        <div
          className="hidden sm:block absolute right-0 top-0 h-full w-24 z-20 pointer-events-none"
          style={{ background: "linear-gradient(90deg, rgb(255 255 255 / 0%) 0%, rgb(255 255 255 / 84%) 50%, rgb(255 255 255) 80%)" }}
        />

        {/* ROW 1 */}
        <div className="overflow-hidden py-2">
          <div className="marquee w-max" style={{ "--speed": "400s" }}>
            {[...row1, ...row1].map((item, i) => <Logo key={i} item={item} />)}
          </div>
        </div>

        {/* ROW 2 */}
        <div className="overflow-hidden py-2">
          <div className="marquee marquee-reverse w-max" style={{ "--speed": "580s" }}>
            {[...row2, ...row2].map((item, i) => <Logo key={i} item={item} />)}
          </div>
        </div>
        {/* ROW 3 */}
        <div className="overflow-hidden py-2">
          <div className="marquee w-max" style={{ "--speed": "300s" }}>
            {[...row3, ...row3].map((item, i) => <Logo key={i} item={item} />)}
          </div>
        </div>

        <div className="w-full flex justify-center mt-10">
          <div
            className="flex flex-row items-center gap-3 cursor-pointer group"
            onClick={() => setOpen(true)}
          >
            <span className="text-lg font-bold text-[#0A2131]">See All Partners</span>
            <div className="bg-[#FFA201] p-2 flex items-center justify-center transition-transform group-hover:translate-x-1">
              <ArrowRight size={20} className="text-white" />
            </div>
          </div>
        </div>
      </div>
      <PartnersModal open={open} onClose={() => setOpen(false)} logos={partnerLogo} />
    </section>
  );
};

function PartnersModal({ open, onClose, logos }) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => setShow(true));
    } else {
      setShow(false);
      const t = setTimeout(() => setMounted(false), 400);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!mounted) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <button
        aria-label="Close modal"
        onClick={onClose}
        className={[
          "absolute inset-0 bg-black/60",
          show ? "opacity-100" : "opacity-0",
        ].join(" ")}
        style={{
          transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        type="button"
      />

      <div className="relative w-full h-full flex items-start justify-center px-3">
        <div
          className={[
            "relative mt-10 md:mt-16 w-[92%] max-w-6xl bg-white rounded-2xl shadow-xl overflow-hidden",
            "will-change-transform will-change-opacity",
            show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-10 scale-90",
          ].join(" ")}
          style={{
            transition: "opacity 400ms cubic-bezier(0.16, 1, 0.3, 1), transform 400ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
          role="dialog"
          aria-modal="true"
          aria-label="All partners"
        >
          <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900">All Partners</h3>
              <p className="text-sm text-slate-600">
                Industry partners recruiting our graduates
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm"
            >
              Close
            </button>
          </div>

          <div className="p-4 md:p-6 max-h-[70vh] overflow-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
              {logos.map((item, idx) => (
                <div
                  key={`${item.altText}-${idx}`}
                  className="bg-white border border-[#efefef] rounded-xl flex items-center justify-center"
                  style={{ minHeight: 84 }}
                >
                  <div className="relative w-full h-20">
                    <Image
                      src={item.img}
                      alt={item.altText}
                      fill
                      sizes="(max-width: 640px) 120px, (max-width: 1024px) 160px, 220px"
                      className="object-contain"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="px-4 md:px-6 py-4 border-t flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrustedPartnersSection;
